import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useMemo, useRef, useState } from "react";

import { normalizeOrderDetailsV2 } from "./utils/normalize";

import { useBreakpointV2 } from "@tm/ui-widgets";
import { ErrorPage } from "../../../ui-container/ErrorPage";
import OrderDetailV2ActionModal from "./components/OrderDetailV2ActionModal";
import { ORDER_DETAIL_V2_ASSETS } from "./constants";
import { usePolicyPolling } from "./hooks/usePolicyPolling";
import { OrderDetailV2Provider } from "./OrderDetailV2Context";
import type {
  OrderDetailV2ActionModalConfig,
  OrderDetailV2WrapperProps,
} from "./types";

// Lazy load device-specific components for code splitting
const OrderDetailV2Desktop = dynamic(() => import("./OrderDetailV2Desktop"), {
  ssr: true,
});

const OrderDetailV2PWA = dynamic(() => import("./OrderDetailV2PWA"), {
  ssr: true,
});

function OrderDetailV2Wrapper({
  data,
  showNav,
  orderActionHandlers,
}: OrderDetailV2WrapperProps) {
  const router = useRouter();

  // ── Polling & order data ───────────────────────────────────────────────
  const { orderData, refreshOrderDetails } = usePolicyPolling({
    data,
    routerQuery: router.query,
  });

  // ── Action modal state ─────────────────────────────────────────────────
  const [actionModal, setActionModal] =
    useState<OrderDetailV2ActionModalConfig | null>(null);

  const showActionModal = useCallback(
    (config: OrderDetailV2ActionModalConfig) => {
      setActionModal(config);
    },
    []
  );

  const openUrl = useCallback((url: string, openInNewTab: boolean) => {
    window.open(url, openInNewTab ? "_blank" : "_self");
  }, []);

  // ── Edit travellers opener (registered by Travellers; called by nudge) ─
  const openEditTravellersRef = useRef<(() => void) | null>(null);
  const registerOpenEditTravellers = useCallback(
    (opener: (() => void) | null) => {
      openEditTravellersRef.current = opener;
    },
    []
  );
  const openEditTravellers = useCallback(() => {
    openEditTravellersRef.current?.();
  }, []);

  // ── Deep-link (?action=<actionId>) ─────────────────────────────────────
  // Pending action in a ref so consume is Strict-Mode safe. URL param is
  // left untouched; each card consumes its own actionId once per load.
  const pendingDeepLinkActionRef = useRef<string | null>(
    typeof router.query["action"] === "string" ? router.query["action"] : null
  );
  const [deepLinkActionId, setDeepLinkActionId] = useState<string | null>(
    () => pendingDeepLinkActionRef.current
  );

  const consumeDeepLinkAction = useCallback((actionId: string) => {
    if (pendingDeepLinkActionRef.current !== actionId) {
      return false;
    }
    pendingDeepLinkActionRef.current = null;
    setDeepLinkActionId(null);
    return true;
  }, []);

  // ── Normalized data (memoized to avoid new object refs on every render) ─
  const currentNormalized = useMemo(
    () => normalizeOrderDetailsV2(orderData),
    [orderData]
  );

  const ctxValue = useMemo(() => {
    return {
      ...currentNormalized,
      refreshOrderDetails,
      orderActionHandlers,
      showActionModal,
      showNav,
      openUrl,
      deepLinkActionId,
      consumeDeepLinkAction,
      openEditTravellers,
      registerOpenEditTravellers,
    };
  }, [
    currentNormalized,
    refreshOrderDetails,
    orderActionHandlers,
    showActionModal,
    showNav,
    openUrl,
    deepLinkActionId,
    consumeDeepLinkAction,
    openEditTravellers,
    registerOpenEditTravellers,
  ]);

  const device = useBreakpointV2();
  const hasContent = currentNormalized.components.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────
  if (!hasContent) {
    return (
      <ErrorPage
        title="Unable to load order details"
        info="We couldn't retrieve your order information. Please try again later."
        errorImg={ORDER_DETAIL_V2_ASSETS.ERROR_IMG}
        headerComponent={<span hidden />}
      />
    );
  }

  return (
    <OrderDetailV2Provider value={ctxValue}>
      {device === "lg" ? (
        <OrderDetailV2Desktop normalizedData={currentNormalized} />
      ) : (
        <OrderDetailV2PWA normalizedData={currentNormalized} />
      )}
      {actionModal && (
        <OrderDetailV2ActionModal
          config={actionModal}
          onClose={() => setActionModal(null)}
        />
      )}
    </OrderDetailV2Provider>
  );
}

export default OrderDetailV2Wrapper;
